/**
 * Detects the user's preferred currency from browser timezone and language.
 *
 * Rules:
 * - Europe/London or language en-GB → GBP
 * - Europe/ (excluding London) or EU-style languages (de, fr, it, es, nl, pt, pl) → EUR
 * - All other regions (including US and rest of world) → USD
 */

const CURRENCY_USD = '$ USD';
const CURRENCY_EUR = '€ EUR';
const CURRENCY_GBP = '£ GBP';

/**
 * Returns the currency string to use based on timezone and navigator.language.
 * @returns {string} One of '$ USD', '€ EUR', '£ GBP'
 */
export function getCurrencyByRegion() {
  const tz = typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : '';
  const lang = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage) || '').toLowerCase();

  // UK: London timezone or en-GB locale → GBP
  if (tz === 'Europe/London' || lang.startsWith('en-gb')) {
    return CURRENCY_GBP;
  }

  // Continental Europe timezone or common EU languages → EUR
  if (
    (tz.startsWith('Europe/') && !tz.includes('London')) ||
    /^de(-|$)/i.test(lang) ||
    /^fr(-|$)/i.test(lang) ||
    /^it(-|$)/i.test(lang) ||
    /^es(-|$)/i.test(lang) ||
    /^nl(-|$)/i.test(lang) ||
    /^pt(-|$)/i.test(lang) ||
    /^pl(-|$)/i.test(lang)
  ) {
    return CURRENCY_EUR;
  }

  // Default: USD for US and all other regions
  return CURRENCY_USD;
}

/** Legacy keys — removed on load so old saves do not block auto-detect */
export const STORAGE_KEY = 'pricing-widget-currency';
export const MANUAL_KEY = 'pricing-widget-currency-manual';

/**
 * Clears legacy localStorage entries from earlier builds that stored
 * manual currency and blocked region detection on reload.
 */
export function clearLegacyCurrencyStorage() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(MANUAL_KEY);
  } catch (_) {}
}

/**
 * Currency on page load: always detect from timezone + language.
 * Manual selector changes apply for the current page only (not persisted).
 * @returns {string} One of '$ USD', '€ EUR', '£ GBP'
 */
export function resolveInitialCurrency() {
  clearLegacyCurrencyStorage();
  return getCurrencyByRegion();
}
